// src/security/claude-subprocess-client.ts
import { spawn } from 'child_process';

export interface ClaudeSubprocessConfig {
  timeout?: number;
  model?: string;
  debug?: boolean;
  outputFormat?: 'text' | 'json' | 'stream-json';
}

export interface ClaudeResponse {
  success: boolean;
  output: string;
  error?: string;
  processingTime: number;
}

export class ClaudeSubprocessClient {
  private config: ClaudeSubprocessConfig;

  constructor(config: ClaudeSubprocessConfig = {}) {
    this.config = {
      timeout: 30000,
      outputFormat: 'text',
      debug: false,
      ...config
    };
  }

  async ask(prompt: string): Promise<ClaudeResponse> {
    const startTime = Date.now();
    
    return new Promise((resolve, reject) => {
      const args = ['--print'];
      
      if (this.config.model) {
        args.push('--model', this.config.model);
      }
      
      if (this.config.outputFormat) {
        args.push('--output-format', this.config.outputFormat);
      }
      
      if (this.config.debug) {
        args.push('--debug');
      }

      const proc = spawn('claude', args, { stdio: 'pipe' });
      let stdout = '';
      let stderr = '';

      // Send the prompt via stdin
      if (proc.stdin) {
        proc.stdin.write(prompt);
        proc.stdin.end();
      }

      proc.stdout?.on('data', (data) => {
        stdout += data.toString();
      });

      proc.stderr?.on('data', (data) => {
        stderr += data.toString();
      });

      proc.on('close', (code) => {
        const processingTime = Date.now() - startTime;
        
        if (code === 0) {
          resolve({
            success: true,
            output: stdout.trim(),
            processingTime
          });
        } else {
          reject({
            success: false,
            output: stdout.trim(),
            error: stderr.trim() || `Claude CLI exited with code ${code}`,
            processingTime
          });
        }
      });

      proc.on('error', (err) => {
        reject({
          success: false,
          output: '',
          error: `Failed to spawn Claude CLI: ${err.message}`,
          processingTime: Date.now() - startTime
        });
      });

      // Timeout handling
      const timeout = setTimeout(() => {
        proc.kill('SIGKILL');
        reject({
          success: false,
          output: stdout.trim(),
          error: `Claude CLI timeout after ${this.config.timeout}ms`,
          processingTime: Date.now() - startTime
        });
      }, this.config.timeout);

      proc.on('close', () => {
        clearTimeout(timeout);
      });
    });
  }

  async classify(message: string): Promise<{
    intent: string;
    confidence: number;
    extractedEntities: Record<string, any>;
    reasoning: string;
  }> {
    const classificationPrompt = `
Analyze this user message and classify the intent for a design system agent.

Message: "${message}"

Classify into one of these intents:
- SHOW_STORY: Navigate to/show a specific component or story
- ANALYZE_STORY: Analyze the current component (colors, props, design choices)
- SEARCH_COMPONENTS: Find components by criteria or search
- ADD_VARIANT: Create a new variant of an existing component
- CREATE_SECTION: Create entirely new components or sections
- VALIDATE_DESIGN: Check design compliance or governance rules
- GENERAL: General conversation or unclear intent

Extract relevant entities:
- componentName: The specific component mentioned (button, card, etc.)
- analysisType: What to analyze (visual, props, accessibility, etc.)
- searchQuery: What to search for
- variantType: Type of variant to create (size, color, state, etc.)

Respond with JSON in this exact format:
{
  "intent": "INTENT_NAME",
  "confidence": 0.85,
  "entities": {
    "componentName": "extracted_component_name_or_null",
    "analysisType": "extracted_analysis_type_or_null",
    "searchQuery": "extracted_search_query_or_null",
    "variantType": "extracted_variant_type_or_null"
  },
  "reasoning": "Brief explanation of why this intent was chosen"
}

Only return valid JSON, no additional text.
`;

    try {
      const response = await this.ask(classificationPrompt);
      const parsed = JSON.parse(response.output);
      
      return {
        intent: parsed.intent || 'GENERAL',
        confidence: parsed.confidence || 0.5,
        extractedEntities: parsed.entities || {},
        reasoning: parsed.reasoning || 'Claude Code classification'
      };
    } catch (error: any) {
      // Fallback if JSON parsing fails
      return {
        intent: 'GENERAL',
        confidence: 0.3,
        extractedEntities: { error: error.message },
        reasoning: 'Failed to parse Claude Code response, using fallback'
      };
    }
  }

  async analyzeComponent(component: string, question: string): Promise<string> {
    const analysisPrompt = `
You are a design system expert. The user is asking about a "${component}" component.

Question: "${question}"

Provide a helpful analysis covering:
1. Design rationale
2. Best practices
3. Common use cases
4. Accessibility considerations

Keep the response concise but informative (2-3 paragraphs).
`;

    try {
      const response = await this.ask(analysisPrompt);
      return response.output;
    } catch (error: any) {
      return `I encountered an issue analyzing the ${component} component: ${error.message || error.error}`;
    }
  }

  // Check if Claude CLI is available
  static async isAvailable(): Promise<boolean> {
    try {
      const proc = spawn('claude', ['--help'], { stdio: 'pipe' });
      return new Promise((resolve) => {
        proc.on('close', (code) => resolve(code === 0));
        proc.on('error', () => resolve(false));
        setTimeout(() => {
          proc.kill();
          resolve(false);
        }, 2000);
      });
    } catch {
      return false;
    }
  }
}